import React from 'react'
import type { TFunction } from 'i18next'
import { Icon } from '../../../atoms/Icon'
import { type TaxAnalysis } from '../taxService'
import { GeographicComparisonCard } from '../cards/GeographicComparisonCard'
import { ProfileDetailsCard } from '../cards/ProfileDetailsCard'
import { TaxRateCard } from '../cards/TaxRateCard'
import { InsightCard } from '../cards/InsightCard'
import {
  TabContentContainer,
  CalculatorLinkBanner,
  CalculatorLinkContent,
  CalculatorLinkTitle,
  CalculatorLinkDescription,
} from '../TaxSection.styles'

export interface OverviewTabProps {
  data: TaxAnalysis | null
  t: TFunction
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ data, t }) => {
  if (!data) return null

  const { current_data, insights, comparison } = data

  return (
    <TabContentContainer>
      {/* Geographic Comparison */}
      {comparison && (
        <GeographicComparisonCard
          data={current_data}
          comparison={comparison}
          t={t}
        />
      )}

      {/* Profile Details Table */}
      <ProfileDetailsCard data={data} t={t} />

      {/* Tax Rate Coefficients */}
      {current_data.income_rate && (
        <TaxRateCard
          data={current_data.income_rate}
          rateComparison={data.rate_comparison}
          t={t}
        />
      )}

      {/* Insight Card */}
      {insights && (
        <InsightCard
          rating={insights.tax_efficiency_rating}
          interpretation={insights.tax_freedom_day_interpretation}
          t={t}
        />
      )}

      {/* Swiss Tax Calculator Link */}
      <CalculatorLinkBanner
        href="https://swisstaxcalculator.estv.admin.ch/#/calculator/income-wealth-tax"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          width="36"
          height="40"
          viewBox="0 0 36 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="1"
            y="1"
            width="34"
            height="38"
            rx="4"
            stroke="currentColor"
            strokeWidth="2"
            opacity="0.6"
          />
          <rect
            x="1"
            y="1"
            width="34"
            height="13"
            rx="4"
            fill="currentColor"
            opacity="0.6"
          />
          <text
            x="18"
            y="11"
            fontFamily="Arial"
            fontSize="8"
            fill="white"
            textAnchor="middle"
            fontWeight="bold"
          >
            TAX
          </text>
          <rect
            x="7"
            y="19"
            width="8"
            height="5"
            rx="1"
            fill="currentColor"
            opacity="0.3"
          />
          <rect
            x="20"
            y="19"
            width="8"
            height="5"
            rx="1"
            fill="currentColor"
            opacity="0.3"
          />
          <rect
            x="7"
            y="28"
            width="8"
            height="5"
            rx="1"
            fill="currentColor"
            opacity="0.3"
          />
          <rect
            x="20"
            y="28"
            width="8"
            height="5"
            rx="1"
            fill="currentColor"
            opacity="0.3"
          />
        </svg>
        <CalculatorLinkContent>
          <CalculatorLinkTitle>{t('calculatorLink.title')}</CalculatorLinkTitle>
          <CalculatorLinkDescription>
            {t('calculatorLink.description')}
          </CalculatorLinkDescription>
        </CalculatorLinkContent>
        <Icon name="external-link" size="small" />
      </CalculatorLinkBanner>
    </TabContentContainer>
  )
}
